import { Check, Lock01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ConnectedBadge,
  IntegrationAccountCard,
  IntegrationProviderHeader,
} from '@/components/integrations/IntegrationCards';
import { useOrg } from '@/contexts/OrgContext';
import { Button } from '@/lib/base/buttons/button';
import {
  FormField,
  FormProvider,
  Input,
  SubmitButton,
  Toggle,
  useFormContext,
} from '@/lib/base/forms';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { ApiError } from '@/lib/errors';
import { sanitizeError } from '@/lib/utils/errors';

import {
  useConnectQuo,
  useDisconnectQuo,
  useQuoStatus,
  useUpdateQuoSettings,
} from './hooks';
import { QuoIcon } from './QuoIcon';
import type { QuoStatus } from './types';

const CONNECT_BENEFITS = [
  'Calls and texts logged on the right contact automatically',
  'Optionally import your existing call and text history',
  'New activity synced to contact timelines in real time',
] as const;

const CONNECT_FORM_FIELDS = { api_key: { required: true } } as const;

function formatDateTime(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// Reconnect never clears backfill_completed_at, so a connection that already
// imported history in a prior session always reads "Imported" even if the
// checkbox was unchecked on a later reconnect.
function backfillStatusLabel(status: QuoStatus): string {
  if (status.backfill_completed_at) {
    return 'Imported';
  }
  if (!status.backfill_enabled) {
    return 'Not imported';
  }
  return 'Importing…';
}

// Inner form body — calls useFormContext so it must be a child of FormProvider.
interface QuoConnectFormBodyProps {
  apiKey: string;
  connectError: string | null;
  onConnected: (lineCount: number) => void;
  connect: ReturnType<typeof useConnectQuo>;
  setApiKey: (v: string) => void;
  setFormError: (v: string | null) => void;
  autoCreate: boolean;
  setAutoCreate: (v: boolean) => void;
  backfill: boolean;
  setBackfill: (v: boolean) => void;
}

function QuoConnectFormBody({
  apiKey,
  connectError,
  onConnected,
  connect,
  setApiKey,
  setFormError,
  autoCreate,
  setAutoCreate,
  backfill,
  setBackfill,
}: QuoConnectFormBodyProps) {
  const { setSubmitError } = useFormContext();
  const connectErrorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (connectError) {
      connectErrorRef.current?.focus();
    }
  }, [connectError]);

  const handleConnect = useCallback(async () => {
    setFormError(null);
    try {
      const res = await connect.mutateAsync({ apiKey, autoCreate, backfill });
      onConnected((res.phone_numbers ?? []).length);
      setApiKey('');
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        (err.response as { error?: string })?.error === 'invalid_api_key'
          ? 'That API key was not accepted. Check it and try again.'
          : sanitizeError(err);
      setSubmitError(msg);
      setFormError(msg);
      setApiKey('');
    }
  }, [
    connect,
    apiKey,
    autoCreate,
    backfill,
    setSubmitError,
    onConnected,
    setApiKey,
    setFormError,
  ]);

  return (
    <div className='space-y-3'>
      {connectError && (
        <div
          ref={connectErrorRef}
          role='alert'
          tabIndex={-1}
          className='bg-utility-error-50 border border-utility-error-200 text-utility-error-700 rounded-lg p-3 text-sm'
        >
          {connectError}
        </div>
      )}
      <FormField name='api_key' label='Quo API key' isRequired>
        {({ isInvalid, showError, inputId }) => (
          <Input
            id={inputId}
            type='password'
            value={apiKey}
            onChange={setApiKey}
            isInvalid={isInvalid}
            showError={showError}
            autoComplete='off'
            placeholder='Paste your Quo API key'
            aria-describedby='quo-api-key-hint'
          />
        )}
      </FormField>
      <p id='quo-api-key-hint' className='text-xs text-tertiary'>
        Create one in Quo under Settings → API.
      </p>
      <div className='flex items-start justify-between gap-4'>
        <div className='flex-1'>
          <p
            className='text-sm font-medium text-[var(--color-text-primary)]'
            id='quo-connect-unknown-numbers-label'
          >
            Create contacts for unknown numbers
          </p>
          <p
            className='text-xs text-tertiary mt-0.5'
            id='quo-connect-unknown-numbers-hint'
          >
            When on, calls and texts from numbers not in your CRM create a new
            contact. When off, that activity stays unlinked to any contact.
          </p>
        </div>
        <Toggle
          isSelected={autoCreate}
          onChange={setAutoCreate}
          size='sm'
          aria-labelledby='quo-connect-unknown-numbers-label'
          aria-describedby='quo-connect-unknown-numbers-hint'
        />
      </div>
      <div className='flex items-start justify-between gap-4'>
        <div className='flex-1'>
          <p
            className='text-sm font-medium text-[var(--color-text-primary)]'
            id='quo-connect-backfill-label'
          >
            Import call & text history
          </p>
          <p
            className='text-xs text-tertiary mt-0.5'
            id='quo-connect-backfill-hint'
          >
            Bring your existing Quo call & text history into Ahoy. Live syncing
            works either way.
          </p>
        </div>
        <Toggle
          isSelected={backfill}
          onChange={setBackfill}
          size='sm'
          aria-labelledby='quo-connect-backfill-label'
          aria-describedby='quo-connect-backfill-hint'
        />
      </div>
      <SubmitButton
        color='primary'
        size='sm'
        onClick={handleConnect}
        isLoading={connect.isPending}
      >
        Connect
      </SubmitButton>
    </div>
  );
}

interface QuoConnectFormProps {
  orgId: number;
  onConnected: (lineCount: number) => void;
  initialAutoCreate: boolean;
}

function QuoConnectForm({
  orgId,
  onConnected,
  initialAutoCreate,
}: QuoConnectFormProps) {
  const connect = useConnectQuo(orgId);
  const [apiKey, setApiKey] = useState('');
  const [connectError, setFormError] = useState<string | null>(null);
  const [autoCreate, setAutoCreate] = useState(initialAutoCreate);
  const [backfill, setBackfill] = useState(true);

  const formValues = useMemo(() => ({ api_key: apiKey }), [apiKey]);

  return (
    <FormProvider fields={CONNECT_FORM_FIELDS} values={formValues}>
      <QuoConnectFormBody
        apiKey={apiKey}
        connectError={connectError}
        onConnected={onConnected}
        connect={connect}
        setApiKey={setApiKey}
        setFormError={setFormError}
        autoCreate={autoCreate}
        setAutoCreate={setAutoCreate}
        backfill={backfill}
        setBackfill={setBackfill}
      />
    </FormProvider>
  );
}

interface QuoConnectCardProps {
  orgId: number;
  onConnected: (lineCount: number) => void;
  title: string;
  subtitle: string;
  showBenefits?: boolean;
  initialAutoCreate?: boolean;
}

// Single unified connect card matching IntegrationAccountCard's visual
// language: identity header, optional benefits list, divided form section,
// and a privacy footer describing what Quo sync actually does (continuous
// webhook + backfill sync — NOT the conversational access model the shared
// IntegrationPrivacyNote describes, which is why that note is not used here).
function QuoConnectCard({
  orgId,
  onConnected,
  title,
  subtitle,
  showBenefits = false,
  initialAutoCreate,
}: QuoConnectCardProps) {
  return (
    <div className='bg-bg-secondary rounded-lg border border-border-secondary overflow-hidden'>
      <div className='p-4'>
        <div className='flex items-center gap-3'>
          <QuoIcon className='w-5 h-5' />
          <div>
            <p className='text-sm font-medium text-[var(--color-text-primary)]'>
              {title}
            </p>
            <p className='text-xs text-tertiary mt-0.5'>{subtitle}</p>
          </div>
        </div>
        {showBenefits && (
          <ul className='mt-4 space-y-2'>
            {CONNECT_BENEFITS.map(text => (
              <li
                key={text}
                className='flex items-start gap-2 text-sm text-[var(--color-text-secondary)]'
              >
                <Check
                  className='size-4 mt-0.5 shrink-0 text-utility-success-600'
                  aria-hidden='true'
                />
                {text}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className='border-t border-border-secondary p-4'>
        <QuoConnectForm
          orgId={orgId}
          onConnected={onConnected}
          initialAutoCreate={initialAutoCreate ?? false}
        />
      </div>
      <div className='border-t border-border-secondary px-4 py-3 flex items-start gap-2'>
        <Lock01
          className='size-4 mt-0.5 shrink-0 text-fg-quaternary'
          aria-hidden='true'
        />
        <p className='text-xs text-tertiary'>
          Your API key is stored encrypted and used only to sync calls and texts
          into your CRM. Disconnecting stops syncing and deletes the key —
          activity already in your CRM stays.
        </p>
      </div>
    </div>
  );
}

export function QuoIntegration() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id ?? 0;

  const { data: status, isLoading, isError, refetch } = useQuoStatus(orgId);
  const updateSettings = useUpdateQuoSettings(orgId);
  const disconnect = useDisconnectQuo(orgId);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [justConnectedLines, setJustConnectedLines] = useState<number | null>(
    null
  );
  const erroredBannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLoading && !isError && status?.status === 'errored') {
      erroredBannerRef.current?.focus();
    }
  }, [isLoading, isError, status?.status]);

  const handleToggle = useCallback(
    async (v: boolean) => {
      setMutationError(null);
      try {
        await updateSettings.mutateAsync(v);
      } catch (err) {
        setMutationError(sanitizeError(err));
      }
    },
    [updateSettings]
  );

  const handleOpenConfirm = useCallback(() => setIsConfirmOpen(true), []);
  const handleCancelDisconnect = useCallback(() => setIsConfirmOpen(false), []);

  const handleConfirmDisconnect = useCallback(async () => {
    setMutationError(null);
    try {
      await disconnect.mutateAsync();
      setJustConnectedLines(null);
      setIsConfirmOpen(false);
    } catch (err) {
      setMutationError(sanitizeError(err));
    }
  }, [disconnect]);

  const handleConnected = useCallback((lineCount: number) => {
    setJustConnectedLines(lineCount);
  }, []);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (!currentOrg?.id) {
    return null;
  }

  const isHealthy = status?.connected && status.status !== 'errored';

  return (
    <div className='space-y-4'>
      <IntegrationProviderHeader
        logo={<QuoIcon className='w-5 h-5' />}
        title='Quo'
      />

      {justConnectedLines !== null && (
        <div
          role='status'
          className='bg-utility-success-50 border border-utility-success-200 text-utility-success-700 rounded-lg p-3 text-sm'
        >
          Quo connected — {justConnectedLines} line(s).
        </div>
      )}

      {isLoading && (
        <div className='flex justify-center py-8'>
          <LoadingIndicator size='sm' />
        </div>
      )}

      {!isLoading && isError && (
        <div className='space-y-3'>
          <p className='text-sm text-utility-error-700'>
            Unable to load Quo status.
          </p>
          <Button color='secondary' size='sm' onClick={handleRetry}>
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !isError && status?.status === 'errored' && (
        <div className='space-y-3'>
          <div
            ref={erroredBannerRef}
            role='alert'
            tabIndex={-1}
            className='bg-utility-error-50 border border-utility-error-200 text-utility-error-700 rounded-lg p-4'
          >
            <p className='font-medium'>Quo needs to reconnect</p>
            <p className='mt-1 text-sm'>
              We couldn&apos;t keep Quo connected. Reconnect it below to
              continue.
            </p>
          </div>
          <QuoConnectCard
            orgId={orgId}
            onConnected={handleConnected}
            title='Reconnect Quo'
            subtitle='Enter a new API key to resume syncing calls and texts.'
            initialAutoCreate={status.auto_create_unknown_contacts}
          />
          <Button color='secondary' size='sm' onClick={handleOpenConfirm}>
            Disconnect
          </Button>
          {mutationError && (
            <div
              role='alert'
              className='bg-utility-error-50 border border-utility-error-200 text-utility-error-700 rounded-lg p-3 text-sm'
            >
              {mutationError}
            </div>
          )}
        </div>
      )}

      {!isLoading && !isError && isHealthy && (
        <div className='space-y-3'>
          <IntegrationAccountCard
            logo={<QuoIcon className='w-5 h-5' />}
            email='Quo phone system'
            subLabel='Capturing calls and texts'
            badges={<ConnectedBadge />}
            actions={
              <Button
                color='secondary'
                size='sm'
                onClick={handleOpenConfirm}
                isDisabled={disconnect.isPending}
              >
                Disconnect
              </Button>
            }
          >
            <div className='border-t border-border-secondary px-4 py-3 grid gap-3 sm:grid-cols-2'>
              <div>
                <p className='text-xs text-tertiary'>Last activity received</p>
                <p className='text-sm text-[var(--color-text-primary)] mt-0.5'>
                  {status.last_webhook_received_at
                    ? formatDateTime(status.last_webhook_received_at)
                    : 'No activity yet'}
                </p>
              </div>
              <div>
                <p className='text-xs text-tertiary'>Call & text history</p>
                <p className='text-sm text-[var(--color-text-primary)] mt-0.5'>
                  {backfillStatusLabel(status)}
                </p>
              </div>
            </div>
            <div className='border-t border-border-secondary px-4 py-3'>
              <div className='flex items-start justify-between gap-4'>
                <div className='flex-1'>
                  <p
                    className='text-sm font-medium text-[var(--color-text-primary)]'
                    id='quo-unknown-numbers-label'
                  >
                    Create contacts for unknown numbers
                  </p>
                  <p
                    className='text-xs text-tertiary mt-0.5'
                    id='quo-unknown-numbers-hint'
                  >
                    When on, calls and texts from numbers not in your CRM create
                    a new contact. When off, that activity stays unlinked to any
                    contact.
                  </p>
                </div>
                <Toggle
                  isSelected={status.auto_create_unknown_contacts}
                  onChange={handleToggle}
                  isDisabled={updateSettings.isPending}
                  aria-labelledby='quo-unknown-numbers-label'
                  aria-describedby='quo-unknown-numbers-hint'
                  size='sm'
                />
              </div>
            </div>
          </IntegrationAccountCard>
          {mutationError && (
            <div
              role='alert'
              className='bg-utility-error-50 border border-utility-error-200 text-utility-error-700 rounded-lg p-3 text-sm'
            >
              {mutationError}
            </div>
          )}
        </div>
      )}

      {!isLoading && !isError && !status?.connected && (
        <QuoConnectCard
          orgId={orgId}
          onConnected={handleConnected}
          title='Connect Quo'
          subtitle='Bring your Quo calls and texts into Ahoy.'
          showBenefits
          initialAutoCreate={false}
        />
      )}

      <ConfirmationDialog
        isOpen={isConfirmOpen}
        variant='warning'
        title='Disconnect Quo'
        description='Ahoy will stop capturing calls and texts from Quo. You can reconnect any time.'
        confirmLabel='Disconnect'
        isLoading={disconnect.isPending}
        instance='disconnect-quo'
        onConfirm={handleConfirmDisconnect}
        onCancel={handleCancelDisconnect}
      />
    </div>
  );
}
